/**
 * Ground shapes remembered per tile (see groundShape): the 5 x 5 pattern
 * key of every tile asked about, so a character's collision step reads it
 * back instead of the 34 tiles round it. Whatever changes a tile says so
 * here (setCell does), and the tiles within two are worked out afresh.
 */
import type { Grid } from "../tiles";

export interface ShapeCache {
  /** Each tile's pattern key, where `state` is KEYED. */
  keys: Int32Array;
  /** UNKNOWN, PLAIN (a whole square, or open with no ground near: no shape) or KEYED. */
  state: Uint8Array;
}

export const UNKNOWN = 0;
export const PLAIN = 1;
export const KEYED = 2;

/** By the tiles themselves, so a grid copied round the same tiles shares it, and it goes when they do. */
const caches = new WeakMap<Uint16Array, ShapeCache>();

export function shapeCacheOf(g: Grid): ShapeCache {
  let c = caches.get(g.tiles);
  if (!c) caches.set(g.tiles, (c = { keys: new Int32Array(g.tiles.length), state: new Uint8Array(g.tiles.length) }));
  return c;
}

/** Tile `i` of `g` changed: forget the shapes it had a say in. */
export function tilesChanged(g: Grid, i: number): void {
  const c = caches.get(g.tiles);
  if (!c) return;
  const x = i % g.w;
  const y = (i / g.w) | 0;
  const x0 = Math.max(0, x - 2);
  const x1 = Math.min(g.w - 1, x + 2);
  for (let cy = Math.max(0, y - 2); cy <= Math.min(g.h - 1, y + 2); cy++) c.state.fill(UNKNOWN, cy * g.w + x0, cy * g.w + x1 + 1);
}
